import { ContextStore, UIStateCallback } from 'components/ImpactAiDrawer/types';

import { createSafeContext } from './scriptValidator';

// Create the worker script as a string
const createWorkerScript = () => `
    // Enhanced isolated JavaScript execution environment with UI callbacks
    
    self.onmessage = function(e) {
      const { script, context } = e.data;
      
      
      try {
        // Remove dangerous globals from worker scope
        delete self.importScripts;
        delete self.close;
        
        // Initialize UI callback storage with limits
        self.uiCallbacks = self.uiCallbacks || {};
        self.uiCallbackCount = self.uiCallbackCount || 0;
        const MAX_UI_CALLBACKS = 10;
        const UI_CALLBACK_TIMEOUT = 30000; // 30 seconds
        
        // Helper to clean up stale callbacks - make it globally available
        self.cleanupCallback = (callbackId) => {
          if (self.uiCallbacks && self.uiCallbacks[callbackId]) {
            delete self.uiCallbacks[callbackId];
            self.uiCallbackCount--;
          }
        };
        
        // Create restricted UI interface for scripts
        const ui = {
          showChoice: (question, choices) => {
            
            if (typeof question !== 'string' || !Array.isArray(choices)) {
              throw new Error('Invalid parameters for showChoice');
            }
            
            // Check callback limit
            if (self.uiCallbackCount >= MAX_UI_CALLBACKS) {
              throw new Error('Too many pending UI requests. Please wait for responses.');
            }
            
            return new Promise((resolve, reject) => {
              const callbackId = Math.random().toString(36);
              
              self.postMessage({ 
                type: 'ui_request', 
                uiType: 'choice',
                question: question.slice(0, 500), // Limit message length
                choices: choices.slice(0, 20).map(c => String(c).slice(0, 100)), // Limit choices
                callbackId 
              });
              
              // Store resolver with timeout
              self.uiCallbacks[callbackId] = resolve;
              self.uiCallbackCount++;
              
              // Auto-cleanup after timeout
              setTimeout(() => {
                if (self.uiCallbacks[callbackId]) {
                  self.cleanupCallback(callbackId);
                  reject(new Error('UI response timeout'));
                }
              }, UI_CALLBACK_TIMEOUT);
            });
          },
          
          showInput: (question, placeholder = '') => {
            
            if (typeof question !== 'string') {
              throw new Error('Invalid parameters for showInput');
            }
            
            // Check callback limit
            if (self.uiCallbackCount >= MAX_UI_CALLBACKS) {
              throw new Error('Too many pending UI requests. Please wait for responses.');
            }
            
            return new Promise((resolve, reject) => {
              const callbackId = Math.random().toString(36);
              
              self.postMessage({ 
                type: 'ui_request', 
                uiType: 'input',
                question: question.slice(0, 500),
                placeholder: String(placeholder).slice(0, 100),
                callbackId 
              });
              
              // Store resolver with timeout
              self.uiCallbacks[callbackId] = resolve;
              self.uiCallbackCount++;
              
              // Auto-cleanup after timeout
              setTimeout(() => {
                if (self.uiCallbacks[callbackId]) {
                  self.cleanupCallback(callbackId);
                  reject(new Error('UI response timeout'));
                }
              }, UI_CALLBACK_TIMEOUT);
            });
          },
          
          showMessage: (message) => {
            
            if (typeof message !== 'string') {
              throw new Error('Invalid parameters for showMessage');
            }
            self.postMessage({ 
              type: 'ui_request', 
              uiType: 'message',
              message: message.slice(0, 1000) // Limit message length
            });
          }
        };


        // Create isolated execution environment with restricted globals
        const restrictedGlobals = {
          // Allow safe JavaScript features
          Array, Object, String, Number, Boolean, Date, Math, JSON,
          parseInt, parseFloat, isNaN, isFinite,
          encodeURIComponent, decodeURIComponent,
          
          // Block dangerous globals
          eval: undefined,
          Function: undefined,
          setTimeout: undefined,
          setInterval: undefined,
          clearTimeout: undefined,
          clearInterval: undefined,
          
          // Provide safe utilities
          console: {
            log: () => {} // No-op for console.log
          }
        };


        // Create async execution environment with restricted scope
        const fn = new Function(
          'context', 
          'ui', 
          'globals',
          \`
            // Destructure safe globals into scope
            const { Array, Object, String, Number, Boolean, Date, Math, JSON, 
                    parseInt, parseFloat, isNaN, isFinite, 
                    encodeURIComponent, decodeURIComponent, console } = globals;
            
            return (async () => {
              \${script}
              
              // If script defines a processData function, call it
              if (typeof processData === 'function') {
                return await processData(context, ui);
              }
              
              // Otherwise return the context
              return context;
            })();
          \`
        );
        
        
        // Execute with restricted scope
        fn(context, ui, restrictedGlobals).then(result => {
          
          // Validate result before sending
          if (result === null || result === undefined) {
            self.postMessage({ success: true, result: null });
            return;
          }
          
          // Ensure result is serializable
          try {
            JSON.stringify(result);
            self.postMessage({ success: true, result });
          } catch (serializationError) {
            self.postMessage({ 
              success: false, 
              error: 'Result is not serializable: ' + serializationError.message 
            });
          }
        }).catch(error => {
          self.postMessage({ 
            success: false, 
            error: error.message || String(error)
          });
        });
        
      } catch (error) {
        self.postMessage({ 
          success: false, 
          error: error.message || String(error)
        });
      }
    };

    // Handle UI responses from main thread
    self.addEventListener('message', function(e) {
      if (e.data.type === 'ui_response') {
        const { callbackId, response } = e.data;
        
        if (self.uiCallbacks && self.uiCallbacks[callbackId]) {
          const resolver = self.uiCallbacks[callbackId];
          // Clean up before resolving to prevent double-cleanup
          if (self.cleanupCallback) {
            self.cleanupCallback(callbackId);
          } else {
            delete self.uiCallbacks[callbackId];
            if (self.uiCallbackCount > 0) self.uiCallbackCount--;
          }
          resolver(response);
        }
      }
    });
  `;

export const executeInWebWorker = (
  script: string,
  context: ContextStore,
  uiStateCallback: UIStateCallback,
  timeout = 30000
): Promise<ContextStore> =>
  new Promise((resolve, reject) => {
    const workerScript = createWorkerScript();
    const blob = new Blob([workerScript], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);

    const worker = new Worker(workerUrl);
    let isResolved = false;
    const pendingUICallbacks = new Set<() => void>();

    const cleanup = () => {
      if (!isResolved) {
        isResolved = true;

        // Clear all pending UI callbacks to prevent memory leaks
        pendingUICallbacks.forEach((callback) => {
          try {
            callback();
          } catch (e) {
            // Ignore errors during cleanup
          }
        });
        pendingUICallbacks.clear();

        worker.terminate();
        URL.revokeObjectURL(workerUrl);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Script execution timeout (${timeout}ms)`));
    }, timeout);

    worker.onmessage = (e) => {
      const {
        type,
        success,
        result,
        error,
        uiType,
        question,
        choices,
        placeholder,
        message,
        callbackId,
      } = e.data;

      if (type === 'ui_request') {
        // Script is requesting UI interaction
        if (uiType === 'choice') {
          // Create a cleanup function for this callback
          const cleanupFn = () => {
            // Send a timeout response to clean up the worker's callback
            if (!isResolved) {
              worker.postMessage({ type: 'ui_response', callbackId, response: null });
            }
          };
          pendingUICallbacks.add(cleanupFn);

          uiStateCallback('script_ui_needed', undefined, {
            type: 'choice',
            question,
            choices,
            onResponse: (response) => {
              pendingUICallbacks.delete(cleanupFn);
              if (!isResolved) {
                worker.postMessage({ type: 'ui_response', callbackId, response });
              }
            },
          });
        } else if (uiType === 'input') {
          const cleanupFn = () => {
            if (!isResolved) {
              worker.postMessage({ type: 'ui_response', callbackId, response: '' });
            }
          };
          pendingUICallbacks.add(cleanupFn);

          uiStateCallback('script_ui_needed', undefined, {
            type: 'input',
            question,
            placeholder,
            onResponse: (response) => {
              pendingUICallbacks.delete(cleanupFn);
              if (!isResolved) {
                worker.postMessage({ type: 'ui_response', callbackId, response });
              }
            },
          });
        } else if (uiType === 'message') {
          uiStateCallback('script_ui_needed', undefined, {
            type: 'message',
            message,
            onResponse: () => {}, // No response needed for messages
          });
        }
      } else {
        // Script execution completed

        clearTimeout(timeoutId);
        cleanup();

        if (success) {
          resolve(result);
        } else {
          reject(new Error(error));
        }
      }
    };

    worker.onerror = (error) => {
      clearTimeout(timeoutId);
      cleanup();
      reject(new Error(`Worker error: ${error.message || String(error)}`));
    };

    // Use safe context to prevent prototype pollution
    const safeContext = createSafeContext(context);

    worker.postMessage({ script, context: safeContext });
  });
